"use client"

import { useState, useRef, useEffect, useMemo } from "react"
import Image from "next/image"
import { ChevronLeft, ChevronRight, Eye } from "lucide-react"
import { PhotoIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline"
import { assets } from "@/utils/assets"
import { useRouter } from "next/navigation"
import { motion, AnimatePresence } from "framer-motion"

import { useEventManagement, ThemeExampleData } from "@/hooks/useEventManagement"
import { useLocale } from "@/context/LocaleContext"
import { cardVariants, gridVariants } from "@/utils/animation";

interface ThemeProp {
  id: number;
  name: string;
  price: string;
  image: string;
  previewUrl: string;
}

const getCardImage = (item: ThemeExampleData & { sampul_image?: string }): string => {
  if (item.image_sampul && typeof item.image_sampul === 'string' && item.image_sampul.trim() !== '') {
    return item.image_sampul.startsWith('http') ? item.image_sampul : assets(item.image_sampul);
  }


  if (item.sampul_image && typeof item.sampul_image === 'string' && item.sampul_image.trim() !== '') {
    return item.sampul_image.startsWith('http') ? item.sampul_image : assets(item.sampul_image);
  }

  if (item.template?.live_demo && typeof item.template.live_demo === 'string' && item.template.live_demo.trim() !== '') {
    return item.template.live_demo.startsWith('http') ? item.template.live_demo : assets(item.template.live_demo);
  }

  return "";
};



function ThemeCard({ theme }: { theme: ThemeProp }) {
  const [imgError, setImgError] = useState(false)
  const router = useRouter()
  const { t, locale } = useLocale();

  const localePrefix = locale === "en" ? "/en" : "";

  useEffect(() => {
    setImgError(false)
  }, [theme.image])

  const handleUseTheme = () => {
    router.push(`${localePrefix}/add-event?idtema=${theme.id}`)
  }

  return (
    <motion.div
      variants={cardVariants()}
      layout
      className="
        rounded-[20px] overflow-hidden 
        border-2 border-gray-100
        shadow-[0_2px_15px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)]
        hover:-translate-y-1 transform transition-shadow duration-300
        bg-white flex flex-col
      "
    >
      <div className="relative w-full h-[230px] overflow-hidden isolate bg-gray-50 flex items-center justify-center">
        {imgError || !theme.image ? (
          <PhotoIcon className="w-12 h-12 text-gray-300" strokeWidth={1.5} />
        ) : (
          <Image
            src={theme.image}
            alt={theme.name}
            fill
            className="object-cover object-top"
            unoptimized
            onError={() => setImgError(true)}
          />
        )}
      </div>

      <div className="bg-white p-3 md:p-4 flex flex-col flex-1 justify-end">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-gray-900 text-[13px] md:text-[15px] truncate pr-2">
            {theme.name || (t("LandingPage.tema_tanpa_nama") || "Tema Tanpa Nama")}
          </h3>
          <span className="font-semibold text-[#E85C57] text-[13px] md:text-[14px]">
            {theme.price}
          </span>
        </div>

        <div className="flex flex-col xl:flex-row gap-1.5 md:gap-2 w-full justify-between">
          <button
            onClick={() => {
              if (theme.previewUrl) window.open(theme.previewUrl, '_blank');
            }}
            className="flex-1 bg-[#F5F5F5] hover:bg-gray-200 text-[#2A2A2A] rounded-full py-2 px-2 text-[11px] lg:text-[11px] font-semibold flex items-center justify-center gap-1.5 transition-colors duration-200 whitespace-nowrap"
          >
            <Eye size={14} strokeWidth={2.5} className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            {t("LandingPage.preview_btn") || "Preview"}
          </button>

          <button
            onClick={handleUseTheme}
            className="flex-1 bg-[#1A1A1A] hover:bg-black text-white rounded-full py-2 px-2 text-[11px] lg:text-[11px] font-semibold flex items-center justify-center gap-1.5 transition-colors duration-200 whitespace-nowrap"
          >
            <PaperAirplaneIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
            {t("LandingPage.gunakan_tema_btn") || "Gunakan Tema"}
          </button>
        </div>
      </div>
    </motion.div>
  )
}

export default function ThemePreview() {
  const { t } = useLocale();
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null)

  const {
    eventTypes,
    fetchEventTypes,
    themesList,
    fetchThemesList,
    isLoadingThemesList
  } = useEventManagement()

  const scrollRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    fetchEventTypes()
  }, [fetchEventTypes])

  const sortedCategories = useMemo(() => {
    return [...eventTypes].sort((a, b) => {
      if (a.jenis_acara.toLowerCase() === 'pernikahan') return -1;
      if (b.jenis_acara.toLowerCase() === 'pernikahan') return 1;
      return 0;
    });
  }, [eventTypes]);

  useEffect(() => {
    if (sortedCategories.length > 0 && activeCategoryId === null) {
      setActiveCategoryId(sortedCategories[0].id)
    }
  }, [sortedCategories, activeCategoryId])

  useEffect(() => {
    if (activeCategoryId !== null) {
      fetchThemesList(activeCategoryId)
    }
  }, [activeCategoryId, fetchThemesList])

  const getIconUrl = (iconPath?: string) => {
    if (!iconPath) return "/images/icons/acara-lainnya.svg";
    if (iconPath.startsWith("http")) return iconPath;
    const safePath = encodeURI(iconPath);
    const finalUrl = assets(safePath).replace(/([^:]\/)\/+/g, "$1");

    return finalUrl;
  }

  const scrollLeft = () =>
    scrollRef.current?.scrollBy({ left: -250, behavior: "smooth" })
  const scrollRight = () =>
    scrollRef.current?.scrollBy({ left: 250, behavior: "smooth" })

  return (
    <main className="min-h-screen bg-white font-sans">
      <section className="py-16 px-4 md:px-24 lg:px-18">
        <div className="max-w-7xl mx-auto">

          <div className="text-center flex flex-col items-center justify-center mb-10 px-4">
            <h2 className="text-[2rem] md:text-[2.5rem] font-bold text-[#2A2A2A] mb-4 tracking-tight">
              {t("LandingPage.preview_theme_title") || "Pilihan Tema Elegan Untuk Berbagai Jenis Acara"}
            </h2>
            <p className="text-[1.05rem] md:text-[1.15rem] text-gray-900 max-w-3xl leading-relaxed font-normal">
              {t("LandingPage.preview_theme_desc_1") || "Setiap tema dirancang dengan tampilan modern, rapi, dan responsif,"}
              <br className="hidden md:block" /> {t("LandingPage.preview_theme_desc_2") || "siap digunakan untuk acara personal hingga profesional."}
            </p>
          </div>

          <div className="relative w-full my-6 flex items-center">
            <button
              onClick={scrollLeft}
              className="absolute -left-2 md:-left-8 z-20 flex items-center bg-[#eceef1] text-black rounded-full p-3 shadow-md hover:bg-[#d7d8da] transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft size={15} />
            </button>

            <div
              ref={scrollRef}
              className="flex overflow-x-auto gap-3 sm:gap-4 px-10 md:px-6 w-full scroll-smooth no-scrollbar cursor-grab active:cursor-grabbing select-none py-2"
              onMouseDown={(e) => {
                const slider = scrollRef.current
                if (!slider) return
                slider.dataset.dragging = "true"
                slider.dataset.startX = e.pageX.toString()
                slider.dataset.scrollLeft = slider.scrollLeft.toString()
              }}
              onMouseLeave={() => {
                const slider = scrollRef.current
                if (slider) slider.dataset.dragging = "false"
              }}
              onMouseUp={() => {
                const slider = scrollRef.current
                if (slider) slider.dataset.dragging = "false"
              }}
              onMouseMove={(e) => {
                const slider = scrollRef.current
                if (!slider || slider.dataset.dragging !== "true") return
                e.preventDefault()
                const startX = parseFloat(slider.dataset.startX || "0")
                const scrollLeft = parseFloat(slider.dataset.scrollLeft || "0")
                const walk = (e.pageX - startX) * 1.5
                slider.scrollLeft = scrollLeft - walk
              }}
              onTouchStart={(e) => {
                const slider = scrollRef.current
                if (!slider) return
                slider.dataset.dragging = "true"
                slider.dataset.startX = e.touches[0].pageX.toString()
                slider.dataset.scrollLeft = slider.scrollLeft.toString()
              }}
              onTouchEnd={() => {
                const slider = scrollRef.current
                if (slider) slider.dataset.dragging = "false"
              }}
              onTouchMove={(e) => {
                const slider = scrollRef.current
                if (!slider || slider.dataset.dragging !== "true") return
                const startX = parseFloat(slider.dataset.startX || "0")
                const scrollLeft = parseFloat(slider.dataset.scrollLeft || "0")
                const walk = (e.touches[0].pageX - startX) * 1.5
                slider.scrollLeft = scrollLeft - walk
              }}
            >
              {sortedCategories.map((cat) => {
                const iconUrl = getIconUrl(cat.icon);
                const isActive = activeCategoryId === cat.id;

                return (
                  <motion.button
                    key={cat.id}
                    onClick={() => setActiveCategoryId(cat.id)}
                    whileTap={{ scale: 0.95 }}
                    animate={{
                      backgroundColor: isActive ? "#e5e7eb" : "#ffffff",
                    }}
                    transition={{ duration: 0.2 }}
                    className={`group flex flex-col items-center justify-center p-3 min-w-[110px] h-[110px] md:min-w-[135px] md:h-[135px] rounded-[16px] shrink-0 ${isActive ? "text-[#2A2A2A]" : "text-[#2A2A2A] hover:border-[#eceef1]"
                      }`}
                  >
                    <div className="w-[75px] h-[75px] mb-2.5 flex items-center justify-center relative transition-transform duration-300 transform group-hover:scale-110 shrink-0">
                      <Image
                        src={iconUrl}
                        alt={cat.jenis_acara}
                        fill
                        unoptimized
                        className="object-contain drop-shadow-sm"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          if (target && !target.src.includes("acara-lainnya.svg")) {
                            target.src = "/images/icons/acara-lainnya.svg";
                          }
                        }}
                      />
                    </div>

                    <span className="text-[13px] md:text-[14px] font-semibold text-center leading-tight">
                      {cat.jenis_acara}
                    </span>
                  </motion.button>
                )
              })
              }

            </div>

            <button
              onClick={scrollRight}
              className="absolute -right-2 md:-right-8 z-20 flex items-center bg-[#eceef1] text-black rounded-full p-3 shadow-md hover:bg-[#d7d8da] transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight size={15} />
            </button>

          </div>

          <div className="mt-10">
            {isLoadingThemesList ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="animate-pulse rounded-[40px] md:rounded-[36px] border-2 border-gray-50 bg-white flex flex-col h-[320px]">
                    <div className="w-full aspect-square bg-gray-100 rounded-[40px] md:rounded-[36px]"></div>
                    <div className="p-4 flex flex-col flex-1 justify-end gap-3">
                      <div className="flex justify-between">
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      </div>
                      <div className="flex gap-2">
                        <div className="h-8 bg-gray-200 rounded-full w-1/2"></div>
                        <div className="h-8 bg-gray-200 rounded-full w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <AnimatePresence mode="wait">
                {themesList.length > 0 && activeCategoryId !== null ? (
                  <motion.div
                    key={activeCategoryId}
                    variants={gridVariants()}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
                  >
                    {themesList.map((item) => (
                      <ThemeCard
                        key={item.id}
                        theme={{
                          id: item.template?.id || item.id,
                          name: item.template?.template_name || "",
                          price: "",
                          image: getCardImage(item),
                          previewUrl: item.template?.url_demo || ""
                        }}
                      />
                    ))}
                  </motion.div>
                ) : (
                  <motion.div
                    key={`empty-${activeCategoryId}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col items-center justify-center py-20 text-center"
                  >
                    <PhotoIcon className="w-16 h-16 text-gray-200 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-800">{t("LandingPage.belum_ada_tema") || "Belum ada tema"}</h3>
                    <p className="text-gray-500 text-sm mt-1">{t("LandingPage.tema_tahap_pembuatan") || "Tema untuk kategori ini sedang dalam tahap pembuatan."}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>

        </div>
      </section>
    </main>
  )
}